// src/pages/Torneios.tsx
import React, { useEffect, useState } from "react";
import { BsPersonArmsUp } from "react-icons/bs";
import { GiTennisCourt } from "react-icons/gi";
import {
  getMenuCollapsedMemory,
  setMenuCollapsedmemory,
  getButtonCollapsedMemory,
  setButtonCollapsedmemory,
  getVersionApp,
} from "../data/memory";
import { useWindowSize } from "../componets/useWindowSize";
import { resetList } from "../data/resetList";
import { autoResetJogosMensal } from "../data/autoClear";
import AlertSuccess from "../componets/AlerSuccess";
import {
  criarTorneio,
  atualizarInscritosTorneio,
  listenTorneio,
} from "../data/torneioService";
import { useNavigate } from "react-router-dom";
import { getAllUsers } from "../data/userService";
import AppShell from "../componets/AppShell";

interface TorneioInfo {
  tipo: "super12" | "super8";
  maxVagas: number;
  quadras: number;
  nome: string;
}

interface UserOption {
  uid: string;
  nome: string;
}

const tiposTorneio: Record<string, TorneioInfo> = {
  super12: { tipo: "super12", maxVagas: 12, quadras: 3, nome: "Super 12" },
  super8: { tipo: "super8", maxVagas: 8, quadras: 2, nome: "Super 8" },
};

const Torneios: React.FC = () => {
  const { width } = useWindowSize();
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(
    getMenuCollapsedMemory()
  );
  const [isButtonCollapsed, setIsButtonCollapsed] = useState(
    getButtonCollapsedMemory()
  );
  const [torneio, setTorneio] = useState<any>(null);
  const [inscritos, setInscritos] = useState<Record<string, any>>({});
  const [dataCriacao, setDataCriacao] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState<
    Record<string, { nome: string }>
  >({});

  const [successAlert, setSuccessAlert] = useState({
    open: false,
    message: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    resetList();
    autoResetJogosMensal();
  }, []);

  useEffect(() => {
    if (width < 1000) {
      setIsMenuCollapsed(true);
      setMenuCollapsedmemory(true);
    } else if (!getButtonCollapsedMemory()) {
      setIsMenuCollapsed(false);
      setMenuCollapsedmemory(false);
    }
  }, [width]);

  useEffect(() => {
    const loadUsers = async () => {
      const allUsers = await getAllUsers();
      const mapped = Object.entries(allUsers || {})
        .filter(
          ([, user]: [string, any]) =>
            user?.bloq !== true && user?.level !== "dev"
        )
        .map(([uid, user]: [string, any]) => ({
          uid,
          nome: user?.displayName || "Usuária sem nome",
        }))
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

      setUsers(mapped);
      if (mapped.length > 0) {
        setSelectedUserId(mapped[0].uid);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    const unsubscribe = listenTorneio((data) => {
      const existe = !!data?.criadoEm;
      setTorneio(data);
      setInscritos(data?.inscritos || {});
      setDataCriacao(data?.criadoEm || null);
      setSelectedParticipants(data?.inscritos || {});
      setLoading(false);

      if (!existe) {
        return;
      }

      if (data.iniciado) {
        navigate("/torneios/jogos");
      } else if (data.tipo) {
        navigate(`/torneios/${data.tipo}`);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleMenuToggle = () => {
    const newCollapsed = !isMenuCollapsed;
    setIsMenuCollapsed(newCollapsed);
    setButtonCollapsedmemory(newCollapsed);
    setMenuCollapsedmemory(newCollapsed);
    setIsButtonCollapsed(!isButtonCollapsed);
  };

  const getMaxVagas = (tipo: "super12" | "super8") =>
    tiposTorneio[tipo].maxVagas;

  const handleAddParticipant = async () => {
    if (!selectedUserId) return;

    const user = users.find((item) => item.uid === selectedUserId);
    if (!user) return;

    const baseTipo = (torneio?.tipo || "super12") as "super12" | "super8";
    const maxVagas = getMaxVagas(baseTipo);

    if (selectedParticipants[selectedUserId]) {
      return;
    }

    if (Object.keys(selectedParticipants).length >= maxVagas) {
      alert(`Esse torneio aceita no máximo ${maxVagas} participantes.`);
      return;
    }

    const updated = {
      ...selectedParticipants,
      [user.uid]: { nome: user.nome },
    };

    setSelectedParticipants(updated);

    if (torneio) {
      await atualizarInscritosTorneio(updated);
    }
  };

  const handleRemoveParticipant = async (uid: string) => {
    const updated = { ...selectedParticipants };
    delete updated[uid];
    setSelectedParticipants(updated);

    if (torneio) {
      await atualizarInscritosTorneio(updated);
    }
  };

  const handleCriarTorneio = async (tipo: "super12" | "super8") => {
    try {
      setBusy(true);
      await criarTorneio(tipo, {});
      setSuccessAlert({
        open: true,
        message: "Torneio criado com sucesso!",
      });
      navigate(`/torneios/${tipo}`);
    } catch (err: any) {
      alert(err?.message || "Falha ao criar o torneio.");
    } finally {
      setBusy(false);
    }
  };

  const torneioInfo = torneio?.tipo ? tiposTorneio[torneio.tipo] : null;

  const vagasDisponiveis = torneioInfo
    ? torneioInfo.maxVagas - Object.keys(inscritos).length
    : 0;

  // Só mostra conteúdo principal quando NÃO está carregando e NÃO está ocupado
  const showContent = !loading && !busy;

  return (
    <>
      <AlertSuccess
        isOpen={successAlert.open}
        message={successAlert.message}
        onClose={() => setSuccessAlert({ open: false, message: "" })}
      />
      <AppShell
        isMenuCollapsed={isMenuCollapsed}
        onToggleMenu={handleMenuToggle}
      >
        {/* Loading (mostra se loading inicial OU ocupado) */}
        {(loading || busy) && (
          <div className="panel-glass mt-2 flex items-center justify-center rounded-[2rem] py-12">
            <div className="flex items-center space-x-2 animate-pulse">
              <div className="h-8 w-8 rounded-full bg-rose-300"></div>
              <div className="h-8 w-8 rounded-full bg-rose-400"></div>
              <div className="h-8 w-8 rounded-full bg-pink-300"></div>
            </div>
          </div>
        )}

        {/* Conteúdo principal só aparece quando não está carregando nem ocupado */}
        {showContent && (
          <>
            <section className="panel-glass rounded-[2rem] p-4 text-white sm:p-8">
              <h2 className="text-xl sm:text-2xl font-semibold mb-6 text-center sm:text-left">
                Torneios
              </h2>

              <div className="panel-soft rounded-[1.5rem] px-4 py-6 text-center">
                {!torneio ? (
                  <>
                    <h1 className="text-lg font-bold mb-3">
                      Escolha o torneio para criar
                    </h1>

                    <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                      {Object.values(tiposTorneio).map((t) => (
                        <div
                          key={t.tipo}
                          className="flex-1 rounded-[1.5rem] border border-white/8 bg-black/20 p-4"
                        >
                          <h2 className="text-xl font-bold mb-2">{t.nome}</h2>
                          <div className="mb-4 flex justify-center gap-6 text-sm text-zinc-300">
                            <div className="flex items-center gap-1">
                              <BsPersonArmsUp className="text-xl" />
                              <span>{t.maxVagas}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <GiTennisCourt className="text-xl" />
                              <span>{t.quadras}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleCriarTorneio(t.tipo)}
                            className="w-full rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-3 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(244,114,182,0.28)] transition hover:brightness-105"
                          >
                            Criar {t.nome}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="text-lg font-bold mb-4">
                      {torneioInfo?.nome || "Torneio"}
                    </h1>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <p className="mb-4">
                        Criado:{" "}
                        {dataCriacao
                          ? new Date(dataCriacao).toLocaleDateString("pt-BR")
                          : "—"}
                      </p>
                      <p className="mb-4">
                        Vagas disponíveis:{" "}
                        {torneioInfo ? vagasDisponiveis : "—"}
                      </p>
                    </div>

                    <div className="mx-auto mb-6 max-w-2xl rounded-[1.5rem] border border-white/8 bg-black/20 p-4 text-left">
                      <label className="mb-2 block text-sm font-medium text-zinc-200">
                        Adicionar participante
                      </label>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <select
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          className="w-full rounded-2xl border border-white/10 bg-white/8 px-4 py-3 text-white outline-none"
                        >
                          {users.map((user) => (
                            <option
                              key={user.uid}
                              value={user.uid}
                              className="text-black"
                            >
                              {user.nome}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleAddParticipant}
                          disabled={
                            !torneioInfo ||
                            Object.keys(selectedParticipants).length >=
                              torneioInfo.maxVagas
                          }
                          className="rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(244,114,182,0.28)] transition hover:brightness-105 disabled:opacity-50"
                        >
                          Adicionar
                        </button>
                      </div>

                      <div className="mt-4">
                        <p className="text-sm text-zinc-300">
                          Participantes atuais: {Object.keys(selectedParticipants).length}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {Object.entries(selectedParticipants).map(
                            ([uid, data]) => (
                              <button
                                key={uid}
                                type="button"
                                onClick={() => handleRemoveParticipant(uid)}
                                className="rounded-full border border-pink-300/30 bg-pink-500/12 px-3 py-1 text-sm text-pink-100 transition hover:bg-pink-500/20"
                              >
                                {data.nome} x
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/torneios/${torneio.tipo}`)}
                      className="w-full rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-3 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(244,114,182,0.28)] transition hover:brightness-105"
                    >
                      Abrir painel do torneio
                    </button>
                  </>
                )}
              </div>
            </section>

            <div className="w-full py-2 text-center">
              <span className="text-xs text-zinc-500 sm:text-sm">
                Developed by Samuel Stork
              </span>{" "}
              <span className="text-xs text-zinc-600 sm:text-sm">
                {getVersionApp()}
              </span>
            </div>
          </>
        )}
      </AppShell>
    </>
  );
};

export default Torneios;
